import { Button, Modal, TextInput } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./file-sharing.scss";
import { baseFilename, SAFE_FILENAME_PATTERN } from "./fileTreeUtils";
import { useAbortControllers } from "./useAbortControllers";
import { hasHttpStatus, ROOT_PATH, SharedFile } from "domain/fileSharing";
import { fileSharingService } from "services/file-sharing/FileSharingService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    item: SharedFile;
    isOpen: boolean;
    onClose: () => void;
    onRenamed: () => void;
}

const RenameFileModal = ({ item, isOpen, onClose, onRenamed }: Props): JSX.Element => {
    const { t } = useTranslation();
    const abortControllers = useAbortControllers();

    const currentBasename = baseFilename(item.filename, item.directoryPath);
    const [newName, setNewName] = React.useState(currentBasename);
    const [validationError, setValidationError] = React.useState<string | undefined>();
    const [renameError, setRenameError] = React.useState<string | undefined>();
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (isOpen) {
            setNewName(currentBasename);
            setValidationError(undefined);
            setRenameError(undefined);
        }
    }, [isOpen, currentBasename]);

    const handleSubmit = React.useCallback(() => {
        const trimmed = newName.trim();
        if (!SAFE_FILENAME_PATTERN.test(trimmed)) {
            setValidationError(t("FileSharing.rename.errorInvalidName"));
            return;
        }
        usageStatisticsService.sendEvent({ category: Category.FILE_SHARING, action: Action.RENAME_FILE });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        setLoading(true);
        setRenameError(undefined);
        fileSharingService
            .renameFile(
                {
                    uuid: item.uuid,
                    new_path: item.directoryPath || ROOT_PATH,
                    new_filename: trimmed,
                    allow_override: false,
                },
                abortController
            )
            .then(() => {
                onRenamed();
                onClose();
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to rename shared file", error);
                    const status = hasHttpStatus(error) ? error.status : undefined;
                    setRenameError(
                        status === 400
                            ? t("FileSharing.rename.errorConflict")
                            : status === 404
                              ? t("FileSharing.rename.errorNotFound")
                              : t("Common.resultErrorMessage")
                    );
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            });
    }, [item.uuid, newName, onRenamed, onClose, t]);

    return (
        <Modal
            isOpen={isOpen}
            hideModal={onClose}
            title={t("FileSharing.rename.title")}
            className={style.dialog}
            buttons={
                <>
                    <Button
                        variant="Secondary"
                        onClick={onClose}
                        disabled={loading}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.cancel")}
                    </Button>
                    <Button
                        variant="Primary"
                        isLoading={loading}
                        disabled={loading}
                        onClick={handleSubmit}
                        testId={testIds.workArea.fileSharing.renameDialog.saveButton}
                    >
                        {t("FileSharing.rename.rename")}
                    </Button>
                </>
            }
        >
            <TextInput
                name="rename-input"
                label={t("FileSharing.rename.label")}
                value={newName}
                onChange={(e) => {
                    setNewName(e.target.value);
                    setValidationError(undefined);
                }}
                testIds={{ input: testIds.workArea.fileSharing.renameDialog.input }}
                disabled={loading}
                error={validationError ?? renameError}
            />
        </Modal>
    );
};

export default RenameFileModal;
